import React, { useEffect, useRef, useState } from 'react';
import { Button, DatePicker, Form, Input, Modal, Select, Switch, Table, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { formatFullTime } from '../../../utils/timeFormat';
import {
  MANAGED_FULFILLMENT_STATUS_LABEL,
  MANAGED_ORDER_STATUS_LABEL,
  MANAGED_PAYMENT_STATUS_LABEL
} from '../orders/orderManageData';
import { GiftPackageModal } from './GiftPackageModal';
import { REGION_OPTIONS } from './packageFormOptions';
import {
  PLAN_PRICE_STATUS_LABEL,
  getPlanDetail,
  getPlanOptions,
  getPlans,
  queryGiftOrders,
  updatePlanAvailability,
  type GiftOrderListItem,
  type GiftOrderListQuery,
  type PlanListItem,
  type PlanPriceSummary
} from './packageManageData';

type TabKey = 'packages' | 'gifts';

type GiftFilterValues = {
  adminName?: string;
  userName?: string;
  planCode?: string;
  giftTimeRange?: [Dayjs, Dayjs];
};

const CODE_21_PATTERN = /^[0-9]{21}$/;

function normalizeCodeFilter(value?: string) {
  const text = value?.trim() ?? '';
  return text || undefined;
}

type GiftStatusTone = 'success' | 'pending' | 'danger' | 'muted' | 'review';

const GIFT_STATUS_TONE_BY_LABEL: Record<string, GiftStatusTone> = {
  已完成: 'success',
  已发放: 'success',
  已支付: 'success',
  待支付: 'pending',
  待发放: 'pending',
  已关闭: 'danger',
  失败: 'danger',
  待人工核对: 'review',
  待处理: 'review',
  无需支付: 'muted'
};

function giftStatusTone(status?: string): GiftStatusTone | '' {
  switch (status?.trim().toUpperCase()) {
    case 'COMPLETED':
    case 'FULFILLED':
    case 'PAID':
      return 'success';
    case 'PENDING':
    case 'UNPAID':
      return 'pending';
    case 'CLOSED':
    case 'FAILED':
      return 'danger';
    case 'MANUAL_REVIEW':
      return 'review';
    case 'NOT_REQUIRED':
      return 'muted';
    default:
      return '';
  }
}

function giftStatusView(status?: string) {
  const raw = status?.trim() ?? '';
  if (!raw) return { label: '—', tone: '' as const };
  const key = /[a-z]/i.test(raw) ? raw.toUpperCase() : raw;
  const label =
    MANAGED_ORDER_STATUS_LABEL[key] ||
    MANAGED_FULFILLMENT_STATUS_LABEL[key] ||
    MANAGED_PAYMENT_STATUS_LABEL[key] ||
    raw;
  return { label, tone: giftStatusTone(key) || GIFT_STATUS_TONE_BY_LABEL[label] || '' };
}

function toGiftTimeBound(day: Dayjs, endOfDay: boolean) {
  const date = day.format('YYYY-MM-DD');
  return endOfDay ? `${date}T23:59:59+08:00` : `${date}T00:00:00+08:00`;
}

function formatMinorAmount(amountMinor?: number, fractionDigits?: number) {
  if (amountMinor == null || !Number.isFinite(amountMinor)) return '—';
  const minor = Math.trunc(amountMinor);
  if (!Number.isSafeInteger(minor)) return String(amountMinor);
  const digits = fractionDigits == null || fractionDigits < 0 ? 0 : Math.min(Math.trunc(fractionDigits), 8);
  const sign = minor < 0 ? '-' : '';
  const abs = Math.abs(minor);
  if (digits === 0) return sign + abs.toLocaleString('en-US');
  const factor = 10 ** digits;
  const whole = Math.trunc(abs / factor);
  const fraction = String(abs % factor).padStart(digits, '0');
  return `${sign}${whole.toLocaleString('en-US')}.${fraction}`;
}

function regionText(regionCode?: string) {
  if (!regionCode) return '—';
  const label = REGION_OPTIONS.find((item) => item.value === regionCode)?.label;
  return label ? `${label} ${regionCode}` : regionCode;
}

function PriceBlocks({ prices }: { prices?: PlanPriceSummary[] }) {
  if (!prices?.length) {
    return <span className='admin-cell-secondary'>暂无价格</span>;
  }
  return (
    <div className='admin-pkg-price-list'>
      {prices.map((item, index) => (
        <div
          key={`${item.regionCode ?? ''}-${item.currency ?? ''}-${item.durationDays ?? ''}-${index}`}
          className='admin-pkg-price-block'
        >
          <div className='admin-pkg-price-main'>
            价格: {formatMinorAmount(item.amountMinor, item.currencyFractionDigits)} {item.currency || '—'}
          </div>
          <div className='admin-pkg-price-sub'>
            {item.durationDays == null ? '—' : `${item.durationDays}天`} · {regionText(item.regionCode)} · 小数位{' '}
            {item.currencyFractionDigits ?? '—'} ·{' '}
            {PLAN_PRICE_STATUS_LABEL[item.status ?? ''] || item.status || '—'}
          </div>
        </div>
      ))}
    </div>
  );
}

export function PackageManage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabKey>('packages');
  const [plans, setPlans] = useState<PlanListItem[]>([]);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [giftForm] = Form.useForm<GiftFilterValues>();
  const [giftFilters, setGiftFilters] = useState<GiftFilterValues>({});
  const [giftOrders, setGiftOrders] = useState<GiftOrderListItem[]>([]);
  const [giftPageNum, setGiftPageNum] = useState(1);
  const [giftPageSize, setGiftPageSize] = useState(20);
  const [giftHasMore, setGiftHasMore] = useState(false);
  const [giftLoading, setGiftLoading] = useState(false);
  const [giftOpen, setGiftOpen] = useState(false);
  const [giftPackageId, setGiftPackageId] = useState<string | undefined>();
  const [planOptions, setPlanOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [planOptionsLoading, setPlanOptionsLoading] = useState(false);
  const [availabilityUpdating, setAvailabilityUpdating] = useState<string[]>([]);
  const availabilityUpdatingRef = useRef(new Set<string>());

  useEffect(() => {
    if (tab !== 'gifts') return;
    let cancelled = false;
    setPlanOptionsLoading(true);
    getPlanOptions()
      .then((res) => {
        if (cancelled) return;
        if (res.code !== 200 || !Array.isArray(res.content)) {
          if (res.code !== -1) message.error(res.message || '套餐选项加载失败');
          setPlanOptions([]);
          return;
        }
        const collected: Array<{ value: string; label: string }> = [];
        res.content.forEach((item) => {
          const planCode = item.planCode?.trim() ?? '';
          if (!CODE_21_PATTERN.test(planCode)) return;
          collected.push({
            value: planCode,
            label: item.displayName?.trim() || planCode
          });
        });
        setPlanOptions(collected);
      })
      .catch(() => {
        if (!cancelled) message.error('套餐选项加载失败');
      })
      .finally(() => {
        if (!cancelled) setPlanOptionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tab]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getPlans({ pageNum, pageSize })
      .then((res) => {
        if (cancelled) return;
        if (res.code !== 200) {
          message.error(res.message || '套餐列表加载失败');
          setPlans([]);
          setTotal(0);
          return;
        }
        const records = res.content?.records;
        const list = Array.isArray(records) ? records : [];
        const pageTotal = res.content?.total;
        setPlans(list);
        setTotal(typeof pageTotal === 'number' ? pageTotal : list.length);
      })
      .catch(() => {
        if (!cancelled) message.error('套餐列表加载失败');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [pageNum, pageSize]);

  useEffect(() => {
    if (tab !== 'gifts') return;
    let cancelled = false;
    setGiftLoading(true);
    const range = giftFilters.giftTimeRange;
    const query: GiftOrderListQuery = {
      pageNum: giftPageNum,
      pageSize: giftPageSize,
      adminName: normalizeCodeFilter(giftFilters.adminName),
      userName: normalizeCodeFilter(giftFilters.userName),
      planCode: normalizeCodeFilter(giftFilters.planCode),
      startTime: range?.[0] ? toGiftTimeBound(range[0], false) : undefined,
      endTime: range?.[1] ? toGiftTimeBound(range[1], true) : undefined
    };
    queryGiftOrders(query)
      .then((res) => {
        if (cancelled) return;
        if (res.code !== 200) {
          if (res.code !== -1) message.error(res.message || '赠送列表加载失败');
          setGiftOrders([]);
          setGiftHasMore(false);
          return;
        }
        setGiftOrders(res.list);
        setGiftHasMore(res.list.length === giftPageSize);
      })
      .catch(() => {
        if (!cancelled) message.error('赠送列表加载失败');
      })
      .finally(() => {
        if (!cancelled) setGiftLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tab, giftPageNum, giftPageSize, giftFilters]);

  const openGiftModal = (packageId?: string) => {
    setGiftPackageId(packageId);
    setGiftOpen(true);
  };

  const searchGiftOrders = (values: GiftFilterValues) => {
    const planCode = normalizeCodeFilter(values.planCode);
    if (planCode && !CODE_21_PATTERN.test(planCode)) {
      message.warning('套餐编码须为 21 位数字');
      return;
    }
    setGiftPageNum(1);
    setGiftFilters({
      adminName: normalizeCodeFilter(values.adminName),
      userName: normalizeCodeFilter(values.userName),
      planCode,
      giftTimeRange: values.giftTimeRange
    });
  };

  const resetGiftFilters = () => {
    giftForm.resetFields();
    setGiftPageNum(1);
    setGiftFilters({});
  };

  const changeAvailability = async (row: PlanListItem, next: { visible?: boolean; purchaseEnabled?: boolean }) => {
    const planCode = row.planCode?.trim() ?? '';
    if (!/^[0-9]{21}$/.test(planCode)) {
      message.warning('套餐编码须为 21 位数字');
      return;
    }
    if (availabilityUpdatingRef.current.has(planCode)) return;
    if (next.visible !== undefined && row.visible === next.visible) return;
    if (next.purchaseEnabled !== undefined && row.purchaseEnabled === next.purchaseEnabled) return;

    availabilityUpdatingRef.current.add(planCode);
    setAvailabilityUpdating((prev) => [...prev, planCode]);
    try {
      const detailRes = await getPlanDetail(planCode);
      if (detailRes.code !== 200 || !detailRes.content?.data?.plan) {
        if (detailRes.code !== -1) message.error(detailRes.message || '套餐详情加载失败');
        return;
      }
      const hash = detailRes.content.configHash;
      if (typeof hash !== 'string' || hash === '') {
        message.warning('缺少配置校验值，请稍后重试');
        return;
      }
      const plan = detailRes.content.data.plan;
      const visible = next.visible !== undefined ? next.visible : plan.visible;
      const purchaseEnabled = next.purchaseEnabled !== undefined ? next.purchaseEnabled : plan.purchaseEnabled;
      if (typeof visible !== 'boolean' || typeof purchaseEnabled !== 'boolean') {
        message.warning('套餐详情缺少展示或购买开关');
        return;
      }
      const res = await updatePlanAvailability(planCode, {
        visible,
        purchaseEnabled,
        expectedConfigHash: hash
      });
      if (res.code !== 200) {
        if (res.code !== -1) {
          message.error(res.message || (res.code === 409 ? '配置已变化，请重新操作' : '设置失败'));
        }
        return;
      }
      const returned = res.content?.data?.plan;
      setPlans((prev) =>
        prev.map((item) => {
          if ((item.planCode?.trim() ?? '') !== planCode) return item;
          return {
            ...item,
            visible: typeof returned?.visible === 'boolean' ? returned.visible : visible,
            purchaseEnabled:
              typeof returned?.purchaseEnabled === 'boolean' ? returned.purchaseEnabled : purchaseEnabled
          };
        })
      );
      message.success(res.message || '已更新');
    } catch {
      message.error('设置失败');
    } finally {
      availabilityUpdatingRef.current.delete(planCode);
      setAvailabilityUpdating((prev) => prev.filter((code) => code !== planCode));
    }
  };

  const confirmDelete = (row: PlanListItem) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定删除套餐「${row.displayName || row.planCode || ''}」吗？删除后不可恢复。`,
      okText: '删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      centered: true,
      onOk: () => {
        setPlans((prev) => prev.filter((item) => item.planCode !== row.planCode));
        message.success(`已删除 ${row.displayName || row.planCode || ''}`);
      }
    });
  };

  const packageColumns: ColumnsType<PlanListItem> = [
    {
      title: '序号',
      width: 64,
      render: (_, __, index) => (pageNum - 1) * pageSize + index + 1
    },
    {
      title: '套餐编码',
      dataIndex: 'planCode',
      width: 200,
      render: (code?: string) => <span className='admin-pkg-code-id'>{code || '—'}</span>
    },
    {
      title: '套餐名称',
      dataIndex: 'displayName',
      width: 120,
      render: (name?: string) => <span className='admin-cell-primary'>{name || '—'}</span>
    },
    {
      title: '套餐等级',
      dataIndex: 'grade',
      width: 88,
      align: 'center',
      render: (grade?: number) => (grade == null ? '—' : grade)
    },
    {
      title: '展示',
      dataIndex: 'visible',
      width: 88,
      align: 'center',
      render: (visible: boolean | undefined, row) => {
        const planCode = row.planCode?.trim() ?? '';
        const busy = availabilityUpdating.includes(planCode);
        return (
          <Switch
            size='small'
            checked={visible === true}
            disabled={visible == null || busy}
            loading={busy}
            onChange={(checked) => changeAvailability(row, { visible: checked })}
          />
        );
      }
    },
    {
      title: '开放购买',
      dataIndex: 'purchaseEnabled',
      width: 100,
      align: 'center',
      render: (enabled: boolean | undefined, row) => {
        const planCode = row.planCode?.trim() ?? '';
        const busy = availabilityUpdating.includes(planCode);
        return (
          <Switch
            size='small'
            checked={enabled === true}
            disabled={enabled == null || busy}
            loading={busy}
            onChange={(checked) => changeAvailability(row, { purchaseEnabled: checked })}
          />
        );
      }
    },
    {
      title: '套餐价格',
      dataIndex: 'prices',
      width: 380,
      render: (prices?: PlanPriceSummary[]) => <PriceBlocks prices={prices} />
    },
    {
      title: '操作',
      key: 'actions',
      width: 200,
      render: (_, row) => (
        <div className='admin-row-actions'>
          <button
            type='button'
            className='admin-link-btn'
            onClick={() => {
              const planCode = row.planCode?.trim() ?? '';
              if (!/^[0-9]{21}$/.test(planCode)) {
                message.warning('套餐编码须为 21 位数字');
                return;
              }
              openGiftModal(planCode);
            }}
          >
            赠送
          </button>
          <button
            type='button'
            className='admin-link-btn'
            onClick={() => {
              const planCode = row.planCode?.trim() ?? '';
              if (!/^[0-9]{21}$/.test(planCode)) {
                message.warning('套餐编码须为 21 位数字');
                return;
              }
              navigate(`/devops/creator-outreach/packages/${planCode}/edit`);
            }}
          >
            编辑
          </button>
          <button
            type='button'
            className='admin-link-btn'
            onClick={() => {
              if (!row.planCode) {
                message.warning('缺少套餐编码');
                return;
              }
              navigate(`/devops/creator-outreach/packages/${row.planCode}`);
            }}
          >
            详情
          </button>
          <button
            type='button'
            className='admin-link-btn admin-link-btn--danger'
            onClick={() => confirmDelete(row)}
          >
            删除
          </button>
        </div>
      )
    }
  ];

  const giftColumns: ColumnsType<GiftOrderListItem> = [
    {
      title: '序号',
      width: 64,
      render: (_, __, index) => (giftPageNum - 1) * giftPageSize + index + 1
    },
    {
      title: '订单号',
      dataIndex: 'orderNo',
      width: 200,
      render: (code?: string) => <span className='admin-pkg-code-id'>{code || '—'}</span>
    },
    {
      title: '赠送人',
      key: 'admin',
      width: 200,
      render: (_, row) => (
        <div>
          <div className='admin-cell-primary'>{row.adminName?.trim() || '—'}</div>
          <div className='admin-cell-secondary admin-pkg-code-id'>{row.adminCode?.trim() || '—'}</div>
        </div>
      )
    },
    {
      title: '被赠送人',
      key: 'user',
      width: 200,
      render: (_, row) => (
        <div>
          <div className='admin-cell-primary'>{row.userName?.trim() || row.displayName?.trim() || '—'}</div>
          <div className='admin-cell-secondary admin-pkg-code-id'>{row.userCode?.trim() || '—'}</div>
        </div>
      )
    },
    {
      title: '套餐',
      key: 'plan',
      width: 200,
      render: (_, row) => (
        <div>
          <div className='admin-cell-primary'>{row.planName?.trim() || '—'}</div>
          <div className='admin-cell-secondary admin-pkg-code-id'>{row.planCode?.trim() || '—'}</div>
        </div>
      )
    },
    {
      title: '天数',
      dataIndex: 'durationDays',
      width: 88,
      align: 'center',
      render: (days?: number) => (days == null ? '—' : `${days}天`)
    },
    {
      title: '原因',
      dataIndex: 'reason',
      width: 220,
      ellipsis: true,
      render: (value?: string) => value?.trim() || '—'
    },
    {
      title: '状态',
      dataIndex: 'orderStatus',
      width: 120,
      render: (_status: string | undefined, row) => {
        const value = row.fulfillmentStatus?.trim() || row.orderStatus?.trim() || '';
        const { label, tone } = giftStatusView(value);
        if (!value) return '—';
        return (
          <span className={`admin-order-status-pill${tone ? ` admin-order-status-pill--${tone}` : ''}`}>
            <i className='admin-order-status-dot' aria-hidden />
            {label}
          </span>
        );
      }
    },
    {
      title: '赠送时间',
      dataIndex: 'giftedAt',
      width: 180,
      render: (value?: string) => formatFullTime(value) || '—'
    }
  ];

  return (
    <div className='admin-page-card'>
      <div className='admin-pkg-toolbar'>
        <div className='admin-pkg-tabs' role='tablist'>
          <button
            type='button'
            role='tab'
            aria-selected={tab === 'packages'}
            className={`admin-pkg-tab${tab === 'packages' ? ' is-active' : ''}`}
            onClick={() => setTab('packages')}
          >
            套餐管理
          </button>
          <button
            type='button'
            role='tab'
            aria-selected={tab === 'gifts'}
            className={`admin-pkg-tab${tab === 'gifts' ? ' is-active' : ''}`}
            onClick={() => setTab('gifts')}
          >
            套餐赠送
          </button>
        </div>
      </div>

      {tab === 'packages' ? (
        <>
          <div className='admin-filter-bar admin-pkg-filter-bar'>
            <Button
              type='primary'
              className='admin-pkg-create-btn admin-pkg-add-btn'
              onClick={() => navigate('/devops/creator-outreach/packages/new')}
            >
              新增
            </Button>
          </div>

          <Table<PlanListItem>
            rowKey={(row) => row.planCode || `${row.displayName ?? ''}-${row.currentRevisionCode ?? ''}`}
            columns={packageColumns}
            dataSource={plans}
            loading={loading}
            scroll={{ x: 1400 }}
            className='admin-user-table'
            size='middle'
            pagination={{
              current: pageNum,
              pageSize,
              total,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (count) => `共 ${count} 条`,
              onChange: (current, size) => {
                const nextSize = Math.min(100, Math.max(1, size));
                if (nextSize !== pageSize) {
                  setPageNum(1);
                  setPageSize(nextSize);
                  return;
                }
                setPageNum(current);
              }
            }}
          />
        </>
      ) : (
        <>
          <Form
            form={giftForm}
            layout='horizontal'
            labelAlign='right'
            colon
            className='admin-order-filter'
            onFinish={searchGiftOrders}
          >
            <div className='admin-order-filter-grid'>
              <Form.Item name='adminName' label='赠送人'>
                <Input allowClear placeholder='请输入赠送人名称' maxLength={128} />
              </Form.Item>
              <Form.Item name='userName' label='被赠送人'>
                <Input allowClear placeholder='请输入被赠送人名称' maxLength={128} />
              </Form.Item>
              <Form.Item name='planCode' label='套餐编码'>
                <Select
                  allowClear
                  showSearch
                  optionFilterProp='label'
                  placeholder='全部'
                  loading={planOptionsLoading}
                  options={planOptions}
                />
              </Form.Item>
              <Form.Item name='giftTimeRange' label='赠送时间'>
                <DatePicker.RangePicker className='w-full' allowEmpty={[true, true]} />
              </Form.Item>
            </div>
            <div className='admin-order-filter-actions'>
              <Button onClick={resetGiftFilters}>重置</Button>
              <Button type='primary' htmlType='submit' className='admin-pkg-query-btn'>
                查询
              </Button>
              <Button
                type='primary'
                className='admin-pkg-create-btn'
                onClick={() => openGiftModal()}
              >
                赠送
              </Button>
            </div>
          </Form>

          <Table<GiftOrderListItem>
            rowKey={(row, index) => row.orderNo || `${row.userCode ?? ''}-${row.planCode ?? ''}-${index}`}
            columns={giftColumns}
            dataSource={giftOrders}
            loading={giftLoading}
            className='admin-user-table'
            size='middle'
            scroll={{ x: 1400 }}
            pagination={{
              current: giftPageNum,
              pageSize: giftPageSize,
              total: giftHasMore
                ? giftPageNum * giftPageSize + 1
                : (giftPageNum - 1) * giftPageSize + giftOrders.length,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (count) => {
                if (giftHasMore) return `第${giftPageNum}页`;
                const pages = Math.max(1, Math.ceil(count / giftPageSize));
                return `共${pages}页`;
              },
              onChange: (current, size) => {
                const nextSize = Math.min(100, Math.max(1, size));
                if (nextSize !== giftPageSize) {
                  setGiftPageNum(1);
                  setGiftPageSize(nextSize);
                  return;
                }
                setGiftPageNum(current);
              }
            }}
          />
        </>
      )}

      <GiftPackageModal
        open={giftOpen}
        plans={plans}
        initialPlanCode={giftPackageId}
        onClose={() => {
          setGiftOpen(false);
          setGiftPackageId(undefined);
        }}
      />
    </div>
  );
}
